import { Testimonial } from '~/components/ui/Testimonial'
import { createFileRoute } from '@tanstack/react-router'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { getTakeActions } from '~/lib/content/content'
import { SocialIcon } from '~/components/icons'
import { SOCIAL_LINKS, TIKTOK_LINK } from '~/lib/site/constants'
import { Container } from '~/components/ui/Container'
import { DonateSignupBand } from '~/components/sections/DonateSignupBand'
import { PageTitle } from '~/components/ui/PageTitle'
import { PageHero } from '~/components/ui/PageHero'

const TITLE = 'Take Action | Direct Action Everywhere'
const DESCRIPTION =
  'DxE is a global grassroots network of animal rights activists. Together, we will achieve social and political change for animals in one generation.'

export const Route = createFileRoute('/take-action')({
  staticData: { minimalHeader: true },
  loader: () => getTakeActions(),
  headers: () => cacheHeaders.content,
  head: () => pageMeta({ title: TITLE, description: DESCRIPTION }),
  component: TakeAction,
})

function TakeAction() {
  const actions = Route.useLoaderData()
  return (
    <>
      <PageHero>
        <Container className="text-center">
          <PageTitle className="mb-5">Take Action for Animals</PageTitle>
          <p className="mb-8 text-base leading-6 md:text-lg md:leading-[26px]">
            Help Us Create Revolutionary Social and Political Change for Animals
            in One Generation
          </p>
          <div className="flex items-center justify-center gap-6 text-accent">
            {[...SOCIAL_LINKS, TIKTOK_LINK].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                className="transition-opacity hover:opacity-70"
              >
                <SocialIcon name={s.name} className="h-8 w-8" />
              </a>
            ))}
          </div>
        </Container>
      </PageHero>

      <section className="py-10">
        <Container className="grid gap-3 lg:grid-cols-2 lg:gap-4">
          {actions.map((a) => (
            <a
              key={a.href}
              href={a.href}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-[15px] rounded-sm bg-surface-alt p-2 text-fg transition-colors hover:bg-surface-alt-hover"
            >
              <img
                {...a.image}
                sizes="100px"
                alt=""
                loading="lazy"
                className="h-[72px] w-[72px] shrink-0 object-cover md:h-[100px] md:w-[100px]"
              />
              <div className="flex-1">
                <h2 className="m-0 text-[15px] leading-5 font-medium text-fg md:text-lg md:leading-6 min-[1200px]:text-xl min-[1200px]:leading-9">
                  {a.title}
                </h2>
                <div className="text-[13px] leading-5 md:text-sm md:leading-6">
                  {a.subtitle}
                </div>
              </div>
              <span className="mr-1 flex h-7 w-7 shrink-0 items-center md:mr-4 md:h-8 md:w-8 justify-center rounded-full bg-surface text-accent">
                <svg aria-hidden="true" viewBox="0 0 12 12" className="h-3 w-3">
                  <path
                    d="M1 1.5l4.5 4.5L1 10.5M6.5 1.5L11 6l-4.5 4.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                  />
                </svg>
              </span>
            </a>
          ))}
        </Container>
      </section>

      <section className="pb-20">
        <Container width="narrow">
          <Testimonial attribution="Alexandra Paul, actress and activist">
            “I feel empowered to make effective change though DxE as it’s like
            no other animal rights organization. It provides continuous
            opportunities for its members to participate actively in helping
            animals. I’m very proud to be involved in a community that reminds
            me I am not alone in believing that animals have rights.”
          </Testimonial>
        </Container>
      </section>

      <DonateSignupBand />
    </>
  )
}
